import CloseOutlined from '@ant-design/icons/CloseOutlined'
import DownloadOutlined from '@ant-design/icons/DownloadOutlined'
import FileTextOutlined from '@ant-design/icons/FileTextOutlined'
import InfoCircleTwoTone from '@ant-design/icons/InfoCircleTwoTone'
import LeftOutlined from '@ant-design/icons/LeftOutlined'
import RightOutlined from '@ant-design/icons/RightOutlined'
import {
  Button,
  Card,
  Checkbox,
  Empty,
  message,
  Modal,
  Spin,
  Tabs,
  Tag,
  Tooltip,
  Upload,
} from 'antd'
import { inject, observer } from 'mobx-react'
import React, { memo, useEffect, useRef, useState } from 'react'
import { Layer, Stage } from 'react-konva'
import { Document, Page } from 'react-pdf'
import { blue } from '../../color'
import { apiUrl } from '../../config'
import {
  DIGITAL_SIGN_PROVIDER,
  DIGITAL_SIGN_STATUS,
  DIGITAL_TYPE_SIGN_SYSTEM,
  DIGITAL_TYPE_SIGN_USER,
  SIGN_VISIBLE,
} from '../../constants'
import utils from '../../utils'
import { trimOverLengthString } from '../Common/CellText'
import EmptyContent from '../EmptyContent'
import SpinnerInlineComponent from '../SpinnerInlineComponent/SpinnerInlineComponent'
import {
  DocumentControllerV2,
  DocumentWrapperV2,
  HeaderPopupSign,
  NameDocument,
  SignatureBox,
  SignDocModalContentWrapperV2,
  ThumbnailsWrapper,
} from './PopupSignStyled'
import RectangleBox from './RectangleBox'

const { TabPane } = Tabs
const CheckboxGroup = Checkbox.Group
const { Meta } = Card
const { confirm } = Modal

const PopupSign = props => {
  const {
    signedDocumentStore,
    loadingAnimationStore,
    fileStore,
    authenticationStore,
    isVisiblePopupSign,
    handleClosePopupSign,
    hiddenSelectDocument,
    handleAfterSign,
  } = props

  let { fileId } = props

  const { currentUser } = authenticationStore
  const { fileBlob, originFileObj, responseSign } = signedDocumentStore

  const [selectedSignature, setSelectedSignature] = useState()
  const [rectangles, setRectangles] = useState([])
  // Sử dụng kích thước PDF thực để render canvas thay vì dùng giá trị cố định
  const [styleCanvas, setStyleCanvas] = useState({ width: 595, height: 842 })
  const [pdfPageSize, setPdfPageSize] = useState({ width: 595, height: 842 })
  const [idImg, setIdImg] = useState()
  const [numPages, setNumPages] = useState()
  const [pageNumber, setPageNumber] = useState(1)
  const [thumbnails, setThumbnails] = useState([])
  const [loadedPdf, setLoadedPdf] = useState()
  const [signatureList, setSignatureList] = useState([])

  // Thêm ref cho Stage
  const stageRef = useRef(null)

  const handleFinishSign = () => {
    setSelectedSignature(null)
    setRectangles([])
    setIdImg(null)
    setPageNumber(1)
    handleClosePopupSign()
    signedDocumentStore.clearDataPopupSign()
    setStyleCanvas({ ...styleCanvas, border: null })
  }

  const [visibleSignature, setVisibleSignature] = useState(SIGN_VISIBLE.VISIBLE)

  const options = [
    {
      key: 1,
      label: 'Ẩn chữ ký',
      value: SIGN_VISIBLE.HIDE,
    },
  ]

  useEffect(() => {
    if (isVisiblePopupSign && currentUser) {
      loadingAnimationStore.setShowSpinInline(true)
      signedDocumentStore
        .getUserSignatureList()
        .then(res => {
          setSignatureList(res.data)
        })
        .finally(() => {
          loadingAnimationStore.setShowSpinInline(false)
        })
    }
  }, [isVisiblePopupSign, currentUser])

  // Tính toán tỷ lệ dựa trên kích thước canvas và PDF thực
  const scaleFactor = styleCanvas.width / pdfPageSize.width

  const handleSelectSignature = signId => {
    if (!signId) return
    setIdImg(signId)

    const img = document.getElementById(signId)
    const imgWidth = img.naturalWidth
    const imgHeight = img.naturalHeight

    const aspectRatio = imgWidth / imgHeight
    const isLandscape = aspectRatio > 1
    const isPortrait = aspectRatio < 1
    const isSquare = aspectRatio === 1

    let calculatedWidth, calculatedHeight
    if (isLandscape) {
      calculatedWidth = imgWidth * 0.4
      calculatedHeight = calculatedWidth / aspectRatio
    } else if (isPortrait) {
      calculatedHeight = imgHeight * 0.4
      calculatedWidth = calculatedHeight * aspectRatio
    } else {
      calculatedWidth = imgWidth * 0.33
      calculatedHeight = calculatedWidth
    }

    setRectangles([
      {
        x: 10, // Tọa độ x, có thể điều chỉnh nếu cần
        y: 10, // Tọa độ y, có thể điều chỉnh nếu cần
        width: calculatedWidth,
        height: calculatedHeight,
        id: 'rect1',
      },
    ])
  }

  const handleSign = async () => {
    loadingAnimationStore.showSpinner(true)
    const formData = new FormData()
    formData.append('file', originFileObj)

    if (!selectedSignature || rectangles.length === 0) {
      message.error('Chưa chọn chữ ký hoặc vị trí ký hợp lệ!')
      loadingAnimationStore.showSpinner(false)
      return
    }

    // Lấy tọa độ và kích thước chữ ký theo kích thước canvas
    const { x, y, width, height } = rectangles[0]

    // Nếu Stage có được kéo, lấy vị trí tuyệt đối của Stage và điều chỉnh tọa độ chữ ký
    let adjustedX = x
    let adjustedY = y
    if (stageRef && stageRef.current) {
      const stagePos = stageRef.current.getAbsolutePosition()
      adjustedX = x - stagePos.x
      adjustedY = y - stagePos.y
    }

    // Chuyển đổi sang tọa độ PDF
    // Lưu ý: Hệ tọa độ PDF có gốc ở góc dưới trái.
    let fnLlx, fnLly, fnUrx, fnUry

    switch (selectedSignature.typeSignature) {
      case 'CIRCLE_SIGN':
        fnLlx = x * scaleFactor + 5
        fnLly = styleCanvas.height - (y + height * scaleFactor) + 7
        fnUrx = fnLlx + width
        fnUry = fnLly + height
        break
      case 'LOGO':
      case 'DEFAULT_SIGN':
        fnLlx = x * scaleFactor + 3
        fnLly = styleCanvas.height - (y + height * scaleFactor) + 5
        fnUrx = fnLlx + width
        fnUry = fnLly + height
        break
      case 'FLASH_SIGN':
      case 'MAIN_SIGN':
        fnLlx = x * scaleFactor
        fnLly = styleCanvas.height - (y + height * scaleFactor)
        fnUrx = fnLlx + width
        fnUry = fnLly + height
        break
      default:
        break
    }

    try {
      if (!fileId) {
        const { data } = await fileStore.uploadFile(formData)
        fileId = data.file_id
      }

      const dataSubmit = {
        reason: 'Signed',
        location: 'VietNam',
        contactInfo: '',
        provider: DIGITAL_SIGN_PROVIDER,
        isVisible: visibleSignature,
        listSignFile: [{ fileId }],
        type:
          selectedSignature.type === DIGITAL_TYPE_SIGN_SYSTEM
            ? DIGITAL_TYPE_SIGN_SYSTEM
            : DIGITAL_TYPE_SIGN_USER,
        image: idImg,
        page: pageNumber,
        typeOfImageSignature: selectedSignature.typeSignature,
        llx: fnLlx,
        lly: fnLly,
        urx: fnUrx,
        ury: fnUry,
      }

      const res = await signedDocumentStore.signDocumentSavis(dataSubmit)
      signedDocumentStore.setResponseSign(res)

      if (handleAfterSign) {
        signedDocumentStore.setResponseSign(undefined)
        await handleAfterSign(res.data.listSignFile[0].fileSignId)
      }
      message.success('Ký số thành công!')
    } catch (err) {
      message.error(err?.vi || 'Ký số thất bại!')
    } finally {
      loadingAnimationStore.showSpinner(false)
    }
  }

  const showSignConfirm = () => {
    if (utils.isNullish(selectedSignature)) {
      message.error('Bạn phải chọn chữ ký trước khi ký')
      return
    }
    if (
      (selectedSignature &&
        selectedSignature.status === DIGITAL_SIGN_STATUS.APPROVAL) ||
      selectedSignature == null
    ) {
      confirm({
        title: 'Bạn có chắc chắn muốn ký tài liệu này?',
        icon: <InfoCircleTwoTone />,
        content: '',
        okText: 'Đồng ý',
        okType: 'primary',
        cancelText: 'Hủy bỏ',
        onOk() {
          handleSign()
        },
        onCancel() {
          setStyleCanvas({ ...styleCanvas, border: null })
          console.log('Hủy ký')
        },
      })
    } else {
      message.info('Chữ ký này chưa được duyệt')
    }
  }

  // Khi chuyển trang PDF, cập nhật lại kích thước canvas dựa trên viewport
  const renderPage = async (pdf, pageNumber) => {
    const page = await pdf.getPage(pageNumber)
    const viewport = page.getViewport({ scale: 1 })
    setStyleCanvas({
      width: viewport.width,
      height: viewport.height,
      border: '1px solid #000',
    })
    setPdfPageSize({ width: viewport.width, height: viewport.height })
  }

  const onDocumentLoadSuccess = pdf => {
    const { numPages } = pdf
    setNumPages(numPages)
    const thumbs = []
    for (let i = 0; i < numPages; i++) {
      thumbs.push(i + 1)
    }
    setThumbnails(thumbs)
    setLoadedPdf(pdf)
    renderPage(pdf, pageNumber)
  }

  const onSelectDocument = file => {
    const newFile = new Blob([file.file], { type: 'application/pdf' })
    signedDocumentStore.setFileBlob(newFile)
    signedDocumentStore.setOriginFileObj(file.file)
  }

  const onSignatureCheckChanged = signature => {
    if (signature === selectedSignature) {
      handleSelectSignature(null)
      setSelectedSignature(null)
      setIdImg(null)
      setRectangles([])
      return
    }
    const signId = signature.signId
    handleSelectSignature(signId)
    setSelectedSignature(signature)
  }

  const onSignVisibleChange = values => {
    if (values.length > 0) {
      setVisibleSignature(SIGN_VISIBLE.HIDE)
    } else {
      setVisibleSignature(SIGN_VISIBLE.VISIBLE)
    }
  }

  return (
    <Modal
      visible={isVisiblePopupSign}
      footer={null}
      width={1400}
      closable={false}
      style={{ top: 20 }}
      wrapClassName="custom-modal-wrapper sign-document-modal"
      title={
        <HeaderPopupSign>
          <span>Ký số tài liệu</span>
          <CloseOutlined
            onClick={handleFinishSign}
            style={{
              width: 24,
              height: 24,
            }}
          />
        </HeaderPopupSign>
      }>
      <SignDocModalContentWrapperV2>
        <div className="document-control-header">
          {fileBlob && loadedPdf && (
            <DocumentControllerV2>
              {!!selectedSignature?.signName && (
                <p style={{ fontSize: '18px', fontWeight: 500, margin: 0 }}>
                  {selectedSignature?.signName}
                </p>
              )}

              {fileBlob && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    marginLeft: 'auto',
                  }}>
                  <Button
                    className="custom-ant-button"
                    style={{ width: 40 }}
                    onClick={() => {
                      if (pageNumber === 1) return
                      setPageNumber(pageNumber - 1)
                      renderPage(loadedPdf, pageNumber - 1)
                    }}
                    icon={<LeftOutlined />}
                  />
                  <p style={{ margin: '0 10px' }}>
                    {pageNumber}/ {numPages}
                  </p>
                  <Button
                    className="custom-ant-button"
                    style={{ width: 40 }}
                    onClick={() => {
                      if (pageNumber === numPages) return
                      setPageNumber(pageNumber + 1)
                      renderPage(loadedPdf, pageNumber + 1)
                    }}
                    icon={<RightOutlined />}
                  />
                </div>
              )}
            </DocumentControllerV2>
          )}
        </div>
        <div className="sign-doc-modal-main-content">
          <div className="left-column">
            <DocumentWrapperV2>
              {originFileObj && loadedPdf && !responseSign && (
                <NameDocument maxWidth={styleCanvas.width}>
                  <Tooltip>
                    <FileTextOutlined
                      style={{ color: blue, fontSize: 16, marginRight: 10 }}
                    />
                    <span style={{ lineHeight: '20px' }}>
                      {trimOverLengthString(originFileObj.name, 70)}
                    </span>
                  </Tooltip>
                </NameDocument>
              )}
              {responseSign && loadedPdf && (
                <NameDocument maxWidth={styleCanvas.width} signed={true}>
                  <Tooltip title={'Tải xuống'}>
                    <div
                      onClick={() =>
                        fileStore.handleDownloadFile(
                          responseSign.data.listSignFile[0].fileSignId,
                          responseSign.data.listSignFile[0].fileSignName
                        )
                      }>
                      <DownloadOutlined
                        style={{
                          color: '#27ae60',
                          fontSize: 16,
                          marginRight: 10,
                        }}
                      />
                      <span style={{ lineHeight: '20px' }}>
                        {trimOverLengthString(
                          responseSign.data.listSignFile[0].fileSignName,
                          70
                        )}
                      </span>
                      <Tag color={'green'} style={{ marginLeft: 8 }}>
                        Đã ký
                      </Tag>
                    </div>
                  </Tooltip>
                </NameDocument>
              )}
              {fileBlob ? (
                <div className={'document-wrapper'}>
                  <Document
                    style={{ display: 'flex', justifyContent: 'center' }}
                    file={fileBlob}
                    onLoadSuccess={onDocumentLoadSuccess}
                    loading={
                      <div className={'spin-box'}>
                        <Spin tip="Đang tải tài liệu" />
                      </div>
                    }>
                    <Page pageNumber={pageNumber} loading={null} />
                  </Document>
                </div>
              ) : (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={'Vui lòng chọn tài liệu ký số!'}
                />
              )}
              {/* Sử dụng ref cho Stage */}
              <Stage
                ref={stageRef}
                width={styleCanvas.width}
                height={styleCanvas.height}
                style={{
                  position: 'absolute',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  border: styleCanvas.border,
                  top: 46,
                  zIndex: 9,
                }}
                draggable={true}>
                <Layer>
                  {rectangles &&
                    rectangles.map((rect, i) => (
                      <RectangleBox
                        key={i}
                        shapeProps={rect}
                        urlImg={`${apiUrl}/api/v1/images/${idImg}`}
                        isSelected={true}
                        onChange={newAttrs => {
                          const updatedRects = [...rectangles]
                          updatedRects[i] = newAttrs
                          setRectangles(updatedRects)
                        }}
                      />
                    ))}
                </Layer>
              </Stage>
            </DocumentWrapperV2>
          </div>
          <div className="right-column">
            {' '}
            <Tabs defaultActiveKey="1">
              <TabPane tab="Tùy chọn ký" key="1">
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ fontWeight: 600, marginRight: 10 }}>
                    Loại ký:{' '}
                  </label>
                  <CheckboxGroup
                    options={options}
                    onChange={onSignVisibleChange}
                  />
                </div>
                <div style={{ position: 'relative' }}>
                  <SpinnerInlineComponent sizeSpin={'small'} />
                  <div>
                    {signatureList ? (
                      signatureList
                        .filter(signature => signature.status !== 'DELETE')
                        .map(signature => (
                          <SignatureBox
                            key={signature.signId}
                            onClick={() => onSignatureCheckChanged(signature)}>
                            <Card
                              hoverable
                              className={'card'}
                              cover={
                                <div className={'img-box'}>
                                  <img
                                    alt="signature"
                                    src={`${apiUrl}/api/v1/images/${signature.signId}`}
                                    id={signature.signId}
                                  />
                                </div>
                              }>
                              <Meta
                                title={
                                  <div
                                    style={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                    }}>
                                    <span>{signature.signName}</span>
                                    <Checkbox
                                      checked={selectedSignature === signature}
                                    />
                                  </div>
                                }></Meta>
                            </Card>
                          </SignatureBox>
                        ))
                    ) : (
                      <EmptyContent
                        description={'Bạn chưa có chữ ký nào, hãy tạo chữ ký!'}
                      />
                    )}
                  </div>
                </div>
              </TabPane>
              <TabPane tab="Phụ lục tài liệu" key="2">
                <ThumbnailsWrapper>
                  <Document
                    noData={
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={'Hãy chọn tài liệu!'}
                      />
                    }
                    file={fileBlob}
                    loading={
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          minHeight: 170,
                        }}>
                        <Spin />
                      </div>
                    }>
                    {thumbnails.map(thumbnailNumber => {
                      return (
                        <div
                          className={
                            thumbnailNumber === pageNumber
                              ? 'thumbnail-box thumbnail-box-active'
                              : 'thumbnail-box'
                          }
                          onClick={() => setPageNumber(thumbnailNumber)}
                          key={thumbnailNumber}>
                          <Page
                            pageNumber={thumbnailNumber}
                            loading={null}
                            width={125}
                          />
                          <div style={{ fontWeight: 'bold' }}>
                            {thumbnailNumber}
                          </div>
                        </div>
                      )
                    })}
                  </Document>
                </ThumbnailsWrapper>
              </TabPane>
            </Tabs>
          </div>
        </div>
        <div
          className="footer-action"
          style={{
            justifyContent: hiddenSelectDocument ? 'center' : 'flex-start',
          }}>
          {!hiddenSelectDocument && (
            <div style={{ marginRight: 'auto' }}>
              <Upload
                accept={'.pdf'}
                beforeUpload={() => false}
                showUploadList={false}
                onChange={onSelectDocument}>
                <Button type="default" className="custom-ant-button w-160">
                  Chọn tài liệu ký số
                </Button>
              </Upload>
            </div>
          )}

          <Button
            danger
            className="custom-ant-button w-160"
            onClick={handleFinishSign}
            htmlType="button">
            Hủy bỏ
          </Button>
          <Button
            title="Ký tài liệu"
            className="custom-ant-button w-160"
            type="primary"
            htmlType="submit"
            onClick={showSignConfirm}>
            Đồng ý
          </Button>
        </div>
      </SignDocModalContentWrapperV2>
    </Modal>
  )
}

PopupSign.propTypes = {}

export default memo(
  inject(
    'signedDocumentStore',
    'loadingAnimationStore',
    'authenticationStore',
    'fileStore'
  )(observer(PopupSign))
)
